import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  importProvidersFrom,
  inject,
  Injector,
  ModuleWithProviders,
  NgModule,
  provideEnvironmentInitializer,
} from '@angular/core';
import { AUTH_SERVICE } from '@core-features/core';
import { BASE_PATH as IDENTITY_SERVICE_GENERATED_BASE_PATH } from '@paldesk/shared-lib/data-access/identity-service-generated';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { AuthHostErrorPresenterService } from './auth-host-error-presenter.service';
import { AuthHostService } from './auth-host.service';
import { AuthLoggerService } from './auth-logger.service';
import { AuthMobileService } from './auth-mobile.service';
import { AuthPublicService } from './auth-public.service';
import { AuthRemoteService } from './auth-remote.service';
import { AUTH_TOKEN_PROVIDER, AuthTokenProvider } from './auth-token-provider';
import { AuthHttpInterceptor } from './auth.interceptor';
import {
  AuthModuleForHostOptions,
  AuthModuleForMobileOptions,
  AuthModuleForRemoteOptions,
} from './models';
import {
  AUTH_HOST_ERROR_PRESENTER,
  AUTH_HOST_LOGGER,
  AUTH_HOST_SERVICE_CONFIG,
  AUTH_IDENTITY_SERVER_URL,
  AUTH_INTERCEPTOR_CONFIG,
} from './tokens';
import { withDefault } from './utils';
import { OAuthWebStorage } from './oauth-web.storage';

const authTokenProviderFactory = (
  injector: Injector,
  securityTokenService: string,
): AuthTokenProvider => ({
  getAccessToken: () => injector.get(AUTH_SERVICE).getAccessToken(),
  isAccessTokenValid: () => injector.get(AUTH_SERVICE).isAccessTokenValid(),
  getTokenEndpoint: () => `${securityTokenService}/connect/token`,
});

@NgModule({})
export class AuthModule {
  static forHost(
    opts: AuthModuleForHostOptions,
  ): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [
        { provide: AUTH_SERVICE, useClass: AuthHostService },
        {
          provide: AUTH_HOST_SERVICE_CONFIG,
          useValue: opts.config,
        },
        withDefault(AUTH_HOST_LOGGER, opts.logger, AuthLoggerService),
        withDefault(
          AUTH_HOST_ERROR_PRESENTER,
          opts.presenter,
          AuthHostErrorPresenterService,
        ),
        { provide: AUTH_INTERCEPTOR_CONFIG, useValue: opts.interceptorConfig },
        {
          provide: AUTH_IDENTITY_SERVER_URL,
          useValue: opts.config.securityTokenService,
        },
        // Configure the generated identity client's base path from the same
        // security-token-service URL. Provided here so consumers (USER_SERVICE)
        // don't have to duplicate this value in every app's providers.
        {
          provide: IDENTITY_SERVICE_GENERATED_BASE_PATH,
          useValue: opts.config.securityTokenService,
        },
        {
          provide: AUTH_TOKEN_PROVIDER,
          useFactory: authTokenProviderFactory,
          deps: [Injector, AUTH_IDENTITY_SERVER_URL],
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthHttpInterceptor,
          multi: true,
        },
        importProvidersFrom(OAuthModule.forRoot()),
        { provide: OAuthStorage, useClass: OAuthWebStorage },
        // Initialize AuthHostService after all dependencies are constructed
        provideEnvironmentInitializer(() => {
          const authService = inject(AUTH_SERVICE) as AuthHostService;
          if (authService.init) {
            authService.init();
          }
        }),
      ],
    };
  }

  static forMobile(
    opts: AuthModuleForMobileOptions,
  ): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [
        { provide: AUTH_SERVICE, useClass: AuthMobileService },
        // Keep the concrete class injectable too — palcode's
        // `MobileSetupService` calls `setOpenUri()`, which is not part of
        // `IAuthService`. Both tokens resolve to the same instance.
        { provide: AuthMobileService, useExisting: AUTH_SERVICE },
        { provide: AUTH_HOST_SERVICE_CONFIG, useValue: opts.config },
        withDefault(AUTH_HOST_LOGGER, opts.logger, AuthLoggerService),
        withDefault(
          AUTH_HOST_ERROR_PRESENTER,
          opts.presenter,
          AuthHostErrorPresenterService,
        ),
        { provide: AUTH_INTERCEPTOR_CONFIG, useValue: opts.interceptorConfig },
        {
          provide: AUTH_IDENTITY_SERVER_URL,
          useValue: opts.config.securityTokenService,
        },
        // Configure the generated identity client's base path from the same
        // security-token-service URL. Provided here so consumers (USER_SERVICE)
        // don't have to duplicate this value in every app's providers.
        {
          provide: IDENTITY_SERVICE_GENERATED_BASE_PATH,
          useValue: opts.config.securityTokenService,
        },
        {
          provide: AUTH_TOKEN_PROVIDER,
          useFactory: authTokenProviderFactory,
          deps: [Injector, AUTH_IDENTITY_SERVER_URL],
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthHttpInterceptor,
          multi: true,
        },
        importProvidersFrom(OAuthModule.forRoot()),
        // Persist OIDC state in `localStorage` so the user stays signed in
        // across Capacitor app restarts (the default `sessionStorage` is
        // cleared every time the WebView is recreated). Matches the legacy
        // `SharedFeatAuthModule.forRoot({ isMobileApp: true })` setup.
        { provide: OAuthStorage, useValue: localStorage },
        // `forMobile` registers both `AUTH_SERVICE` and `AuthMobileService` as
        // the same instance just above, so we can inject the concrete class
        // directly here without a type assertion.
        provideEnvironmentInitializer(() => {
          inject(AuthMobileService).init();
        }),
      ],
    };
  }

  static forRemote(opts: AuthModuleForRemoteOptions) {
    return {
      ngModule: AuthModule,
      providers: [
        { provide: AUTH_SERVICE, useClass: AuthRemoteService },
        { provide: AUTH_INTERCEPTOR_CONFIG, useValue: opts.interceptorConfig },
        {
          provide: AUTH_IDENTITY_SERVER_URL,
          useValue: opts.config.securityTokenService,
        },
        // Configure the generated identity client's base path from the same
        // security-token-service URL. Provided here so consumers (USER_SERVICE)
        // don't have to duplicate this value in every app's providers.
        {
          provide: IDENTITY_SERVICE_GENERATED_BASE_PATH,
          useValue: opts.config.securityTokenService,
        },
        {
          provide: AUTH_TOKEN_PROVIDER,
          useFactory: authTokenProviderFactory,
          deps: [Injector, AUTH_IDENTITY_SERVER_URL],
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthHttpInterceptor,
          multi: true,
        },
      ],
    };
  }

  /**
   * For public apps with no user login (e.g. Solutions Configurator). Provides
   * an always-unauthenticated AUTH_SERVICE with no OIDC/interceptor wiring —
   * such apps authenticate their API calls through their own gateway config.
   */
  static forPublic(): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [{ provide: AUTH_SERVICE, useClass: AuthPublicService }],
    };
  }
}
